/**
 * The rules the report (S80), the map (S81) and the day 90 read (S82) show numbers by, as pure functions so each one
 * is unit tested (report.test.ts) and the screens only lay them out:
 *
 *   a number that wasn't measured reads as its reason, never as 0 (docs/44 §1.3 rule 10)
 *   a change is a count, with a percentage only when the contract carries one (last cycle of 10 or more)
 *   the change map compares two grids point by point: gained, lost, kept or the same
 */
import { S } from '@ff/app-ui/copy';

const R = S.reports;

/** The parts of a contract `ReportNumber` the display rules read. */
export interface CycleNumber {
  thisCycle: number | null;
  lastCycle: number | null;
  change: { delta: number; percent: number | null } | null;
  notMeasured: string | null;
}

export interface NumberValue {
  /** False when there's no number this cycle: `text` is then the reason, in words. */
  measured: boolean;
  text: string;
}

/** This cycle's value. A gap reads as its reason ("Not measured this cycle"), never as 0 and never as a dash. */
export function numberValue(n: Pick<CycleNumber, 'thisCycle' | 'notMeasured'>): NumberValue {
  if (n.thisCycle === null) return { measured: false, text: n.notMeasured?.trim() || R.notMeasured };
  return { measured: true, text: count(n.thisCycle) };
}

/** Last cycle's value, or null when there wasn't one (the change line then says there's nothing to compare). */
export function lastCycleValue(n: Pick<CycleNumber, 'lastCycle'>): string | null {
  return n.lastCycle === null ? null : count(n.lastCycle);
}

/**
 * The change in words. Nothing when either side is missing, except "No earlier cycle to compare" when this cycle has
 * a number and the last one doesn't. A percentage shows only when the contract gives one: with last cycle under 10
 * it's null and the count stands alone, so 2 to 4 never reads as "up 100%".
 */
export function changeText(n: CycleNumber): string | null {
  if (n.thisCycle === null) return null;
  if (n.lastCycle === null) return R.noEarlier;
  const c = n.change;
  if (!c) return null;
  if (c.delta === 0) return R.noChange;
  const by = Math.abs(c.delta);
  // A percentage that rounds to 0 on a real change says less than the count, so the count stands alone.
  const percent = c.percent === null || !Number.isFinite(c.percent) || c.percent === 0 ? null : Math.abs(c.percent);
  if (c.delta > 0) return percent === null ? R.changeMore(by) : R.changeUp(by, percent);
  return percent === null ? R.changeFewer(by) : R.changeDown(by, percent);
}

/** Which way the change points, for the arrow beside the words. Null when there's no change to show. */
export function changeTrend(n: CycleNumber): 'up' | 'down' | 'flat' | null {
  if (n.thisCycle === null || n.lastCycle === null || !n.change) return null;
  return n.change.delta > 0 ? 'up' : n.change.delta < 0 ? 'down' : 'flat';
}

export const NUMBER_GROUPS = ['website', 'google', 'reviews'] as const;
export type NumberGroup = (typeof NUMBER_GROUPS)[number];

/** The supporting numbers by group, in the fixed group order and the contract's order inside each. Empty groups drop out. */
export function groupNumbers<T extends { group: string }>(numbers: readonly T[]): Array<{ group: NumberGroup; items: T[] }> {
  return NUMBER_GROUPS.map((group) => ({ group, items: numbers.filter((x) => x.group === group) })).filter((g) => g.items.length > 0);
}

/** A whole count with thousands separators: 1,204. */
export function count(v: number): string {
  return Number.isFinite(v) ? Math.round(v).toLocaleString('en-US') : R.notMeasured;
}

/** Top three points of 81. An average of two scans can sit on a half: 21.5. A whole number shows as one. */
export function points(v: number): string {
  if (!Number.isFinite(v)) return R.notMeasured;
  const r = Math.round(v * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

// ------------------------------------------------------------------------------------------ the change map

export type Rank = number | null;
export type ChangeCell = 'gained' | 'lost' | 'kept' | 'same';

const inTopThree = (r: Rank | undefined): boolean => typeof r === 'number' && r >= 1 && r <= 3;

/** One point, day one against now: in the top three now and not then, then and not now, both times, or neither. */
export function classifyChange(dayOne: Rank | undefined, latest: Rank | undefined): ChangeCell {
  const was = inTopThree(dayOne);
  const is = inTopThree(latest);
  if (is && !was) return 'gained';
  if (was && !is) return 'lost';
  return is ? 'kept' : 'same';
}

/**
 * Every point of two grids compared, in the grids' own order. Null when the two aren't the same shape: a client's
 * grid never changes after day one, so a mismatch means the data is wrong and no comparison is drawn.
 */
export function changeCells(dayOne: readonly Rank[], latest: readonly Rank[]): ChangeCell[] | null {
  if (dayOne.length === 0 || dayOne.length !== latest.length) return null;
  return dayOne.map((r, i) => classifyChange(r, latest[i]));
}

export interface ChangeCounts {
  gained: number;
  lost: number;
  kept: number;
  same: number;
  /** Gained less lost: how many more points are in the top three now. */
  net: number;
}

export function changeCounts(cells: readonly ChangeCell[]): ChangeCounts {
  const c = { gained: 0, lost: 0, kept: 0, same: 0 };
  for (const x of cells) c[x]++;
  return { ...c, net: c.gained - c.lost };
}

/** "In the top three at 9 more points than day one", from the two maps drawn. */
export function changeSummary(c: Pick<ChangeCounts, 'net'>): string {
  if (c.net > 0) return R.changeMoreThan(c.net);
  if (c.net < 0) return R.changeFewerThan(Math.abs(c.net));
  return R.changeSameAs;
}

/** The mark each change cell carries beside its colour, so colour is never the only signal. */
export const CHANGE_SYMBOL: Record<ChangeCell, string> = { gained: '+', lost: '−', kept: '•', same: '·' };

/** Rows of a square grid, north row first. */
export function rowsOf<T>(cells: readonly T[], size: number): T[][] {
  if (!(size > 0)) return [];
  return Array.from({ length: Math.ceil(cells.length / size) }, (_, r) => cells.slice(r * size, (r + 1) * size));
}

// ------------------------------------------------------------------------------------------ the day 90 read

export type Day90Outcome = 'kept' | 'missed_eligible' | 'missed_not_eligible';

/** The outcome chip: its words and its tone. Never a promise that money is on its way. */
export function day90Chip(outcome: Day90Outcome): { tone: 'ok' | 'warn' | 'neutral'; text: string } {
  const text = S.day90.outcome[outcome];
  return { tone: outcome === 'kept' ? 'ok' : outcome === 'missed_eligible' ? 'warn' : 'neutral', text };
}
